import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  DollarSign,
  Plug,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { Card, CardHeader, CardBody, CardTitle } from '@/components/ui/Card';
import { Tabs } from '@/components/ui/Tabs';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';
import type { CommandCenter } from '@/services/marketingIntegrations.service';
import {
  formatMoney,
  formatNumber,
  summarizeChannels,
  tagCampaign,
  type ActionTag,
  type CampaignWithAction,
} from './derive';
import { useMetaAdsEnabled } from '@/pages/marketing/metaAds';

interface Props {
  cc: CommandCenter;
  targetCpa: number;
}

const TAG_STYLES: Record<ActionTag, { bg: string; text: string; ring: string }> = {
  SCALE: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  KEEP:  { bg: 'bg-blue-50',    text: 'text-blue-700',    ring: 'ring-blue-200' },
  CUT:   { bg: 'bg-red-50',     text: 'text-red-700',     ring: 'ring-red-200' },
  FIX:   { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200' },
};

function ActionBadge({ action }: { action: ActionTag }) {
  const s = TAG_STYLES[action];
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold tracking-wide ring-1', s.bg, s.text, s.ring)}>
      {action}
    </span>
  );
}

function cpaCellTone(cpa: number | null, target: number): string {
  if (cpa == null) return 'text-slate-400';
  if (cpa <= target) return 'text-emerald-700';
  if (cpa <= target * 2) return 'text-amber-700';
  return 'text-red-700';
}

/**
 * Which channel a view is scoped to. `null` means "every connected channel",
 * i.e. the combined view this tab has always shown.
 */
type ChannelFilter = 'Google Ads' | 'Meta Ads' | null;

export default function PaidMediaTab({ cc, targetCpa }: Props) {
  const { enabled: metaAdsEnabled } = useMetaAdsEnabled();
  const gads = cc.dashboard.google_ads;
  const meta = cc.dashboard.meta_ads;
  const noPaid = !gads?.configured && !meta?.configured;

  if (noPaid) {
    return (
      <div className="p-6">
        <EmptyState
          icon={<Plug className="h-8 w-8 text-slate-400" />}
          title="No paid-ad provider connected"
          description={`Connect Google Ads${metaAdsEnabled ? ' or Meta Ads' : ''} to see channel performance, action tags, and budget reallocation.`}
          action={{ label: 'Connect a provider', onClick: () => { window.location.href = '/admin/marketing-integrations'; } }}
        />
      </div>
    );
  }

  // Split Google and Meta into their own tabs. Only worth the chrome when both
  // are actually connected — a clinic running Google alone would otherwise get
  // a tab strip where one tab is permanently empty.
  const bothConnected = !!gads?.configured && !!meta?.configured;
  if (!bothConnected) {
    return <ChannelFindings cc={cc} targetCpa={targetCpa} channel={null} />;
  }

  return (
    <Tabs
      // No horizontal padding here: the tab bar already carries px-5 and each
      // panel body its own p-6, so adding more would step the strip in twice.
      className="pt-3"
      tabs={[
        { id: 'all',    label: 'All channels', content: <ChannelFindings cc={cc} targetCpa={targetCpa} channel={null} /> },
        { id: 'google', label: 'Google',       content: <ChannelFindings cc={cc} targetCpa={targetCpa} channel="Google Ads" /> },
        { id: 'meta',   label: 'Meta',         content: <ChannelFindings cc={cc} targetCpa={targetCpa} channel="Meta Ads" /> },
      ]}
    />
  );
}

/**
 * The findings body, scoped to one channel or to all of them. Everything that
 * is per-campaign or per-channel filters; the closing architecture guidance is
 * channel-agnostic advice and is deliberately left whole.
 */
function ChannelFindings({ cc, targetCpa, channel }: Props & { channel: ChannelFilter }) {
  const channelSummaries = summarizeChannels(cc.dashboard, targetCpa)
    .filter((c) => channel === null || c.channel === channel);
  const taggedCampaigns: CampaignWithAction[] = (cc.campaigns?.rows ?? [])
    .filter((c) => channel === null || c.channel === channel)
    .map((c) => tagCampaign(c, targetCpa));
  const cuts = taggedCampaigns.filter((c) => c.action === 'CUT' || c.action === 'FIX');
  const scaleTargets = taggedCampaigns.filter((c) => c.action === 'SCALE');
  const wastedTotal = cuts.reduce((s, c) => s + c.wastedSpend, 0);

  // On a single-channel tab the Channel column repeats the tab label on every
  // row, and a fetch error for the OTHER channel is not this tab's problem.
  const showChannelColumn = channel === null;
  const errorKey = channel === 'Google Ads' ? 'google_ads' : channel === 'Meta Ads' ? 'meta_ads' : null;
  const visibleErrors = Object.entries(cc.campaigns?.errors ?? {})
    .filter(([k]) => errorKey === null || k === errorKey);

  return (
    <div className="space-y-6 p-6">
      {/* ── Channel summary cards w/ action tags ─────────────────── */}
      <section>
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Channels</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {channelSummaries.map((c) => (
            <Card key={c.channel}>
              <CardBody>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{c.channel}</div>
                    <div className="text-2xl font-bold text-slate-900 tabular-nums mt-1">
                      {c.cpa != null ? formatMoney(c.cpa) : '—'}
                    </div>
                    <div className="text-xs text-slate-500">CPA</div>
                  </div>
                  <ActionBadge action={c.action} />
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-slate-500">Spend</div>
                    <div className="font-semibold tabular-nums">{formatMoney(c.spend)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500">Conversions</div>
                    <div className="font-semibold tabular-nums">{formatNumber(Math.round(c.conversions))}</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-700 leading-relaxed">{c.reason}</div>
              </CardBody>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Campaign performance table ───────────────────────────── */}
      <Card>
        <CardHeader
          action={
            <Link to="/admin/marketing-integrations">
              <Button variant="ghost" size="sm"><Plug className="h-4 w-4 mr-1" />Manage providers</Button>
            </Link>
          }
        >
          <CardTitle>Campaign ledger</CardTitle>
        </CardHeader>
        <CardBody className="-mx-4 -mb-4 sm:-mx-5 sm:-mb-5">
          {visibleErrors.length > 0 && (
            <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-900 flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{visibleErrors.map(([k, v]) => `${k}: ${v}`).join(' · ')}</span>
            </div>
          )}
          {taggedCampaigns.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-slate-500">
              No {channel === null ? '' : `${channel === 'Google Ads' ? 'Google' : 'Meta'} `}campaigns reported activity in the last 30 days.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-2.5 text-left font-semibold">Campaign</th>
                    {showChannelColumn && <th className="px-3 py-2.5 text-left font-semibold">Channel</th>}
                    <th className="px-3 py-2.5 text-right font-semibold">Spend</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Conv.</th>
                    <th className="px-3 py-2.5 text-right font-semibold">CPA</th>
                    <th className="px-3 py-2.5 text-right font-semibold">ROAS</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Action</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Why</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {taggedCampaigns.map((c) => (
                    <tr key={`${c.channel}-${c.id}`} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-medium text-slate-900 max-w-[260px] truncate" title={c.name}>{c.name}</td>
                      {showChannelColumn && (
                        <td className="px-3 py-2.5">
                          <Badge variant="info" size="sm">{c.channel === 'Google Ads' ? 'Google' : 'Meta'}</Badge>
                        </td>
                      )}
                      <td className="px-3 py-2.5 text-right tabular-nums">{formatMoney(c.spend)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{formatNumber(Math.round(c.conversions))}</td>
                      <td className={cn('px-3 py-2.5 text-right tabular-nums font-semibold', cpaCellTone(c.cpa, targetCpa))}>
                        {c.cpa != null ? formatMoney(c.cpa) : '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{c.roas != null ? `${c.roas.toFixed(1)}×` : '—'}</td>
                      <td className="px-3 py-2.5"><ActionBadge action={c.action} /></td>
                      <td className="px-3 py-2.5 text-xs text-slate-600 max-w-[260px]">{c.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ── Wasted spend flags ─────────────────────────────────────── */}
      {cuts.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
            Underperforming campaigns
            <span className="ml-2 text-red-600">{formatMoney(wastedTotal)} estimated waste</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cuts.map((c) => (
              <div key={`waste-${c.channel}-${c.id}`} className="rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{c.channel} · {formatMoney(c.spend)} spend · {formatNumber(Math.round(c.conversions))} conv.</div>
                    <div className="text-xs text-slate-700 mt-2">{c.reason}</div>
                  </div>
                  <div className="text-right">
                    <ActionBadge action={c.action} />
                    <div className="mt-2 text-xs text-red-700 font-bold tabular-nums">{formatMoney(c.wastedSpend)} wasted</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Budget reallocation ────────────────────────────────── */}
      {(cuts.length > 0 || scaleTargets.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-600" />
              Budget reallocation
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-red-700 font-bold mb-2 flex items-center gap-1">
                  <TrendingDown className="h-3.5 w-3.5" /> Free up from CUT/FIX
                </div>
                <div className="text-2xl font-bold text-red-700 tabular-nums">{formatMoney(wastedTotal)}</div>
                <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
                  {cuts.slice(0, 5).map((c) => (
                    <li key={`re-cut-${c.id}`} className="flex justify-between gap-3">
                      <span className="truncate">{c.name}</span>
                      <span className="tabular-nums text-red-600">{formatMoney(c.wastedSpend)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-emerald-700 font-bold mb-2 flex items-center gap-1">
                  <TrendingUp className="h-3.5 w-3.5" /> Redeploy to SCALE
                </div>
                {scaleTargets.length === 0 ? (
                  <div className="text-sm text-slate-500 italic">No SCALE-tagged campaigns yet — first identify a winner before redeploying.</div>
                ) : (
                  <ul className="space-y-1.5 text-sm text-slate-700">
                    {scaleTargets.slice(0, 5).map((c) => (
                      <li key={`re-scale-${c.id}`} className="flex justify-between gap-3">
                        <span className="truncate">{c.name}</span>
                        <span className="tabular-nums text-emerald-700">{(c.roas ?? 0).toFixed(1)}× ROAS</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            {scaleTargets.length > 0 && (
              <div className="mt-4 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900 flex items-center gap-2">
                <ArrowRight className="h-4 w-4 flex-shrink-0" />
                Redirect <strong>{formatMoney(wastedTotal)}</strong> from cuts into <strong>{scaleTargets[0].name}</strong> at its current {(scaleTargets[0].roas ?? 0).toFixed(1)}× ROAS.
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* ── Recommended architecture ────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-blue-600" />
            Recommended campaign architecture
          </CardTitle>
        </CardHeader>
        <CardBody>
          <ol className="space-y-3 text-sm text-slate-700">
            <li><strong className="text-slate-900">1. Brand defense first.</strong> Always-on exact-match brand search at low spend, high ROAS — protects the existing demand layer.</li>
            <li><strong className="text-slate-900">2. Scale your winners.</strong> {scaleTargets.length > 0
              ? `Double the budget on ${scaleTargets[0].name} (${(scaleTargets[0].roas ?? 0).toFixed(1)}× ROAS). Watch CPA at the new spend tier.`
              : 'When a campaign hits 5×+ ROAS at a meaningful spend tier, it earns a 2× budget bump — re-evaluate weekly.'}
            </li>
            <li><strong className="text-slate-900">3. One controlled experiment per channel.</strong> Cap test spend at 15% of channel budget. Kill at the 4-week mark if CPA &gt; 2× target.</li>
            <li><strong className="text-slate-900">4. Conversion API before more spend.</strong> Server-side conversion sharing (Google enhanced conversions, Meta CAPI) is non-negotiable before scaling.</li>
            <li><strong className="text-slate-900">5. Retargeting follow-on.</strong> Site-visitor + engaged-video retargeting layered onto the top prospecting funnel — typically delivers the lowest CPA in the account.</li>
          </ol>
        </CardBody>
      </Card>
    </div>
  );
}
